import axios from "axios";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
    Table,
    Spin,
    Typography,
    Input,
    Select,
    DatePicker,
    Row,
    Col,
    Modal,
    Space,
    Tag,
} from "antd";
import { EditOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import * as XLSX from "xlsx";
import { Button } from "antd";
import EmployeeProfile from "../EmployeeProfile";

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

export default function EmpPermissions() {
    const [permissions, setPermissions] = useState([]);
    const [filteredPermissions, setFilteredPermissions] = useState([]);
    const [loading, setLoading] = useState(false);

    const [selectedId, setSelectedId] = useState(null)
    const [showEmp, setShowEmp] = useState(false)

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPermission, setSelectedPermission] = useState(null);
    const [newStatus, setNewStatus] = useState("");
    const [cancelReason, setCancelReason] = useState("");

    const { authData, backUrl } = useContext(AuthContext);

    const [Organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    // Bulk Action States
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [showBulkApprove, setShowBulkApprove] = useState(false);
    const [showBulkReject, setShowBulkReject] = useState(false);
    const [bulkRemarks, setBulkRemarks] = useState("");
    const [bulkProcessing, setBulkProcessing] = useState(false);

    const [filters, setFilters] = useState({
        name: "",
        emp_code: "",
        session: "",
        status: "",
        dateRange: [],

        // Added cascading filters
        organisation: "",
        department: "",
        designation: "",
    });

    // Fetch main permission records
    const fetchPermissions = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backUrl}/api/hr/permissions/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            const data = res.data?.results || res.data;
            setPermissions(data);
            setFilteredPermissions(data);
        } catch (error) {
            console.error("Error fetching permissions:", error);
            toast.error("Failed to load permission data");
        } finally {
            setLoading(false);
        }
    };

    // Fetch Organisations on load
    const fetchOrganisation = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            const results = res.data?.results ?? res.data ?? [];
            setOrganisations(results);
        } catch (err) {
            console.error("Error fetching organisations:", err);
            toast.error("Failed to load organisations");
        }
    };

    useEffect(() => {
        fetchPermissions();
        fetchOrganisation();
    }, []);

    // Fetch departments when organisation changes
    useEffect(() => {
        const org = filters.organisation;

        if (org) {
            fetchDepartments(org);
        } else {
            setDepartments([]);
            setDesignations([]);

            setFilters((prev) => ({
                ...prev,
                department: "",
                designation: "",
            }));
        }
    }, [filters.organisation]);

    // Fetch designations when department changes
    useEffect(() => {
        const dept = filters.department;

        if (dept) {
            fetchDesignations(dept);
        } else {
            setDesignations([]);
            setFilters((prev) => ({ ...prev, designation: "" }));
        }
    }, [filters.department]);

    const fetchDepartments = async (organisationId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/departments/?organisation=${organisationId}`,
                {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                }
            );
            const results = res.data?.results ?? res.data ?? [];
            setDepartments(results);
        } catch (err) {
            console.error("Error fetching departments:", err);
            toast.error("Failed to load departments");
        }
    };

    const fetchDesignations = async (departmentId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/designations/?department=${departmentId}`,
                {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                }
            );
            const results = res.data?.results ?? res.data ?? [];
            setDesignations(results);
        } catch (err) {
            console.error("Error fetching designations:", err);
            toast.error("Failed to load designations");
        }
    };

    // ============================
    // Apply Filters Automatically
    // ============================

    useEffect(() => {
        let result = [...permissions];

        if (filters.name) {
            result = result.filter((item) =>
                item?.employee?.first_name
                    ?.toLowerCase()
                    .includes(filters.name.toLowerCase())
            );
        }

        if (filters.emp_code) {
            result = result.filter((item) =>
                item?.employee?.emp_code
                    ?.toLowerCase()
                    .includes(filters.emp_code.toLowerCase())
            );
        }

        if (filters.session) {
            result = result.filter(
                (item) =>
                    item.session?.toLowerCase() ===
                    filters.session.toLowerCase()
            );
        }

        if (filters.status) {
            result = result.filter(
                (item) =>
                    item.status?.toLowerCase() ===
                    filters.status.toLowerCase()
            );
        }

        if (filters.dateRange?.length === 2) {
            const [start, end] = filters.dateRange;

            result = result.filter((item) => {
                const itemDate = dayjs(item.date);
                return (
                    itemDate.isAfter(dayjs(start).subtract(1, "day")) &&
                    itemDate.isBefore(dayjs(end).add(1, "day"))
                );
            });
        }

        // Cascade filters applied on the list
        if (filters.organisation) {
            result = result.filter(
                (item) =>
                    String(item?.employee?.organisation) ===
                    String(filters.organisation)
            );
        }

        if (filters.department) {
            result = result.filter(
                (item) =>
                    String(item?.employee?.department) ===
                    String(filters.department)
            );
        }

        if (filters.designation) {
            result = result.filter(
                (item) =>
                    String(item?.employee?.designation) ===
                    String(filters.designation)
            );
        }

        setFilteredPermissions(result);
    }, [filters, permissions]);

    // ============================
    // Edit Modal
    // ============================

    const handleEditClick = (record) => {
        setSelectedPermission(record);
        setNewStatus(record.status);
        setCancelReason("");
        setIsModalOpen(true);
    };

    const downloadExcel = () => {
        if (!filteredPermissions.length) {
            toast.error("No permission records available to download.");
            return;
        }

        const rows = filteredPermissions.map((item) => ({
            "Employee Name": `${item?.employee?.first_name ?? ""} ${item?.employee?.last_name ?? ""}`.trim(),

            "Employee Code": item?.employee?.emp_code ?? "-",

            Organisation: item?.employee?.organisation_name ?? "-",

            Department: item?.employee?.department_name ?? "-",

            Designation: item?.employee?.designation_name ?? "-",

            "Hours Applied": item?.hours_requested ?? "-",

            Time: item?.time ?? "-",

            Session: item?.session ?? "-",

            Date: item.date
                ? new Date(item.date).toLocaleDateString("en-IN")
                : "-",

            Reason: item?.reason ?? "-",
        }));

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workbook, worksheet, "Permissions");

        XLSX.writeFile(workbook, "Employee_Permissions.xlsx");
    };

    const handleModalOk = async () => {
        if (newStatus === "Cancelled" && !cancelReason.trim()) {
            toast.error("Please provide a cancellation reason.");
            return;
        }

        try {
            await axios.post(
                `${backUrl}/api/hr/permissions/${selectedPermission.id}/cancel/`,
                { cancellation_reason: cancelReason },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );

            toast.success("Permission status updated successfully.");
            fetchPermissions();
        } catch (error) {
            toast.error("Failed to update permission status.");
        }

        setIsModalOpen(false);
        setSelectedPermission(null);
    };

    const handleModalCancel = () => {
        setIsModalOpen(false);
        setSelectedPermission(null);
    };

    // ============================
    // Bulk Approve Handler
    // ============================
    const handleBulkApprove = async () => {
        if (selectedRowKeys.length === 0) {
            toast.warning("Please select at least one record");
            return;
        }

        setBulkProcessing(true);

        try {
            const bulkApproveApi = `${backUrl}/api/hr/permissions/approve/`;

            const payload = {
                permission_ids: selectedRowKeys,
                remarks: bulkRemarks
            };

            const res = await axios.post(bulkApproveApi, payload, {
                headers: {
                    Authorization: `Bearer ${authData?.access}`,
                },
            });

            const successMsg =
                res?.data?.message ||
                res?.data?.detail ||
                "Permissions approved successfully";

            toast.success(successMsg);

            // Reset states
            setBulkRemarks("");
            setShowBulkApprove(false);
            setSelectedRowKeys([]);

            // Refresh data
            fetchPermissions();

        } catch (error) {
            const errorMsg =
                error?.response?.data?.error ||
                error?.response?.data?.detail ||
                "Bulk approval failed";
            toast.error(errorMsg);
        } finally {
            setBulkProcessing(false);
        }
    };

    // ============================
    // Bulk Reject Handler
    // ============================
    const handleBulkReject = async () => {
        if (selectedRowKeys.length === 0) {
            toast.warning("Please select at least one record");
            return;
        }

        if (!bulkRemarks.trim()) {
            toast.warning("Please provide a reason for rejection");
            return;
        }

        setBulkProcessing(true);

        try {
            const bulkRejectApi = `${backUrl}/api/hr/permissions/reject/`;

            const payload = {
                permission_ids: selectedRowKeys,
                rejection_reason: bulkRemarks
            };

            const res = await axios.post(bulkRejectApi, payload, {
                headers: {
                    Authorization: `Bearer ${authData?.access}`,
                },
            });

            const successMsg =
                res?.data?.message ||
                res?.data?.detail ||
                "Permissions rejected successfully";

            toast.success(successMsg);

            // Reset states
            setBulkRemarks("");
            setShowBulkReject(false);
            setSelectedRowKeys([]);

            // Refresh data
            fetchPermissions();

        } catch (error) {
            const errorMsg =
                error?.response?.data?.error ||
                error?.response?.data?.detail ||
                "Bulk rejection failed";
            toast.error(errorMsg);
        } finally {
            setBulkProcessing(false);
        }
    };

    // ============================
    // Row Selection Configuration
    // ============================
    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedKeys) => {
            setSelectedRowKeys(selectedKeys);
        },
        getCheckboxProps: (record) => ({
            name: record.id,
        }),
    };

    // ============================
    // Table Columns
    // ============================

    const onBack = () => {
        setShowEmp(false);
    };


    const columns = [
        {
            title: "Employee Code",
            dataIndex: ["employee", "emp_code"],
            key: "emp_code",
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <a
                        onClick={() => {
                            setSelectedId(record.employee.id);
                            setShowEmp(true);
                        }}
                        style={{ cursor: "pointer", color: '#1890ff' }}
                    >
                        {text}
                    </a>
                </div>
            ),
            height: 30,
            width: 180,
        },
        {
            title: "Employee Name",
            dataIndex: ["employee", "first_name"],
            key: "name",
            fixed: "left",
        },

        {
            title: "Organisation",
            dataIndex: ["employee", "organisation_name"],
            key: "org",
        },
        {
            title: "Department",
            dataIndex: ["employee", "department_name"],
            key: "dept",
        },
        {
            title: "Designation",
            dataIndex: ["employee", "designation_name"],
            key: "desg",
        },
        {
            title: "Hours Applied",
            dataIndex: "hours_requested",
            key: "hours_requested",
        },
        {
            title: "Time",
            dataIndex: "time",
            key: "time",
        },
        {
            title: "Session",
            dataIndex: "session",
            key: "session",
        },
        {
            title: "Date",
            dataIndex: "date",
            key: "date",
            render: (date) =>
                date ? new Date(date).toLocaleDateString("en-IN") : "",
        },
        {
            title: "Reason",
            dataIndex: "reason",
            key: "reason",
        },
        // {
        //     title: "Action",
        //     key: "action",
        //     render: (_, record) => (
        //         <EditOutlined
        //             style={{ color: "#0e497b", fontSize: "18px", cursor: "pointer" }}
        //             onClick={() => handleEditClick(record)}
        //         />
        //     ),
        //     fixed: "right",
        // },
    ];

    return (
        <div style={{ padding: "20px" }}>

            {
                showEmp ? (
                    <EmployeeProfile empId={selectedId} onBack={onBack} DisableEditOption={true} />
                ) : (
                    <>
                        <ToastContainer />

                        <Title
                            level={4}
                            style={{
                                marginBottom: 16,
                                textTransform: "uppercase",
                                color: "#0e497b",
                            }}
                        >
                            Employee Permission Records
                        </Title>

                        {/* FILTER BOX */}
                        <div
                            style={{
                                background: "#fff",
                                padding: "15px",
                                borderRadius: "10px",
                                marginBottom: "15px",
                            }}
                        >
                            <Row gutter={[16, 16]}>
                                {/* Name */}
                                <Col xs={24} sm={12} md={6}>
                                    <Input
                                        placeholder="Employee Name"
                                        value={filters.name}
                                        onChange={(e) =>
                                            setFilters({
                                                ...filters,
                                                name: e.target.value,
                                            })
                                        }
                                    />
                                </Col>

                                {/* Code */}
                                <Col xs={24} sm={12} md={6}>
                                    <Input
                                        placeholder="Employee Code"
                                        value={filters.emp_code}
                                        onChange={(e) =>
                                            setFilters({
                                                ...filters,
                                                emp_code: e.target.value,
                                            })
                                        }
                                    />
                                </Col>

                                {/* Session */}
                                <Col xs={24} sm={12} md={6}>
                                    <Select
                                        placeholder="Session"
                                        value={filters.session || undefined}
                                        onChange={(value) =>
                                            setFilters({ ...filters, session: value })
                                        }
                                        allowClear
                                        style={{ width: "100%" }}
                                    >
                                        {[...new Set(permissions.map((p) => p.session))].map(
                                            (type) => (
                                                <Option key={type} value={type}>
                                                    {type}
                                                </Option>
                                            )
                                        )}
                                    </Select>
                                </Col>

                                {/* Status */}
                                <Col xs={24} sm={12} md={6}>
                                    <Select
                                        placeholder="Status"
                                        value={filters.status || undefined}
                                        onChange={(value) =>
                                            setFilters({ ...filters, status: value })
                                        }
                                        allowClear
                                        style={{ width: "100%" }}
                                    >
                                        <Option value="Approved">Approved</Option>
                                        <Option value="Pending">Pending</Option>
                                    </Select>
                                </Col>

                                {/* Organisation */}
                                <Col xs={24} sm={12} md={6}>
                                    <Select
                                        placeholder="Organisation"
                                        value={filters.organisation || undefined}
                                        onChange={(value) =>
                                            setFilters({ ...filters, organisation: value })
                                        }
                                        allowClear
                                        style={{ width: "100%" }}
                                    >
                                        {Organisations.map((o) => (
                                            <Option key={o.id} value={o.id}>
                                                {o.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Col>

                                {/* Department */}
                                <Col xs={24} sm={12} md={6}>
                                    <Select
                                        placeholder="Department"
                                        value={filters.department || undefined}
                                        onChange={(value) =>
                                            setFilters({ ...filters, department: value })
                                        }
                                        allowClear
                                        disabled={!filters.organisation}
                                        style={{ width: "100%" }}
                                    >
                                        {departments.map((d) => (
                                            <Option key={d.id} value={d.id}>
                                                {d.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Col>

                                {/* Designation */}
                                <Col xs={24} sm={12} md={6}>
                                    <Select
                                        placeholder="Designation"
                                        value={filters.designation || undefined}
                                        onChange={(value) =>
                                            setFilters({ ...filters, designation: value })
                                        }
                                        allowClear
                                        disabled={!filters.department}
                                        style={{ width: "100%" }}
                                    >
                                        {designations.map((dg) => (
                                            <Option key={dg.id} value={dg.id}>
                                                {dg.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Col>

                                {/* Date Range */}
                                <Col xs={24} sm={24} md={8}>
                                    <RangePicker
                                        format="DD/MM/YYYY"
                                        value={filters.dateRange}
                                        onChange={(dates) =>
                                            setFilters({
                                                ...filters,
                                                dateRange: dates,
                                            })
                                        }
                                        style={{ width: "100%" }}
                                    />
                                </Col>

                                <Col xs={24} sm={12} md={6}>
                                    <Button
                                        type="primary"
                                        style={{ background: "#0e497b", borderColor: "#0e497b", width: "100%" }}
                                        onClick={downloadExcel}
                                    >
                                        Download Excel
                                    </Button>
                                </Col>
                            </Row>
                        </div>

                        {/* Bulk Action Buttons */}
                        {selectedRowKeys.length > 0 && (
                            <div style={{ marginBottom: 16, textAlign: "center" }}>
                                <Space>
                                    <Tag color="blue">{selectedRowKeys.length} selected</Tag>
                                    {/* <Button
                            type="primary"
                            icon={<CheckOutlined />}
                            style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
                            onClick={() => setShowBulkApprove(true)}
                        >
                            Bulk Approve
                        </Button>
                        <Button
                            danger
                            type="primary"
                            icon={<CloseOutlined />}
                            onClick={() => setShowBulkReject(true)}
                        >
                            Bulk Reject
                        </Button> */}
                                    <Button onClick={() => setSelectedRowKeys([])}>
                                        Clear Selection
                                    </Button>
                                </Space>
                            </div>
                        )}

                        {/* TABLE */}
                        {loading ? (
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    height: "50vh",
                                }}
                            >
                                <Spin size="large" />
                            </div>
                        ) : (
                            <Table
                                columns={columns}
                                dataSource={filteredPermissions}
                                rowKey={(record) => record.id}
                                rowSelection={rowSelection}
                                bordered
                                pagination={{ pageSize: 10 }}
                                scroll={{ x: "max-content" }}
                                components={{
                                    header: {
                                        cell: (props) => (
                                            <th
                                                {...props}
                                                style={{
                                                    backgroundColor: "#0e497b",
                                                    color: "white",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {props.children}
                                            </th>
                                        ),
                                    },
                                }}
                            />
                        )}

                        {/* EDIT MODAL */}
                        <Modal
                            title="Edit Permission Status"
                            open={isModalOpen}
                            onOk={handleModalOk}
                            onCancel={handleModalCancel}
                            okText="Save"
                            centered
                            okButtonProps={{
                                style: {
                                    backgroundColor: "#0e497b",
                                    borderColor: "#0e497b",
                                    color: "#fff",
                                },
                            }}
                        >
                            <p>
                                <strong>Employee:</strong>{" "}
                                {selectedPermission?.employee?.first_name} (
                                {selectedPermission?.employee?.emp_code})
                            </p>

                            <Select
                                value={newStatus}
                                onChange={(value) => setNewStatus(value)}
                                style={{ width: "100%", marginBottom: 12 }}
                            >
                                <Option value="Cancelled">Cancelled</Option>
                            </Select>

                            {newStatus === "Cancelled" && (
                                <Input.TextArea
                                    placeholder="Enter reason for cancellation"
                                    value={cancelReason}
                                    onChange={(e) => setCancelReason(e.target.value)}
                                    rows={3}
                                    style={{
                                        borderColor: cancelReason.trim()
                                            ? "#d9d9d9"
                                            : "red",
                                    }}
                                />
                            )}
                        </Modal>

                        {/* Bulk Approve Modal */}
                        <Modal
                            title={`Bulk Approve (${selectedRowKeys.length} permissions)`}
                            open={showBulkApprove}
                            footer={null}
                            onCancel={() => {
                                setShowBulkApprove(false);
                                setBulkRemarks("");
                            }}
                            width={600}
                            centered
                        >
                            <Input.TextArea
                                rows={4}
                                placeholder="Enter common remarks for all selected permissions (optional)..."
                                value={bulkRemarks}
                                onChange={(e) => setBulkRemarks(e.target.value)}
                                style={{ borderRadius: "8px", marginBottom: "16px" }}
                            />
                            <div style={{ textAlign: "right" }}>
                                <Button
                                    style={{ marginRight: "8px" }}
                                    onClick={() => {
                                        setShowBulkApprove(false);
                                        setBulkRemarks("");
                                    }}
                                    disabled={bulkProcessing}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="primary"
                                    loading={bulkProcessing}
                                    disabled={bulkProcessing}
                                    style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
                                    onClick={handleBulkApprove}
                                >
                                    {bulkProcessing ? "Approving..." : "Approve All"}
                                </Button>
                            </div>
                        </Modal>

                        {/* Bulk Reject Modal */}
                        <Modal
                            title={`Bulk Reject (${selectedRowKeys.length} permissions)`}
                            open={showBulkReject}
                            footer={null}
                            onCancel={() => {
                                setShowBulkReject(false);
                                setBulkRemarks("");
                            }}
                            width={600}
                            centered
                        >
                            <Input.TextArea
                                rows={4}
                                placeholder="Enter common rejection reason for all selected permissions..."
                                value={bulkRemarks}
                                onChange={(e) => setBulkRemarks(e.target.value)}
                                style={{ borderRadius: "8px", marginBottom: "16px" }}
                            />
                            <div style={{ textAlign: "right" }}>
                                <Button
                                    style={{ marginRight: "8px" }}
                                    onClick={() => {
                                        setShowBulkReject(false);
                                        setBulkRemarks("");
                                    }}
                                    disabled={bulkProcessing}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="primary"
                                    danger
                                    loading={bulkProcessing}
                                    disabled={bulkProcessing || !bulkRemarks.trim()}
                                    onClick={handleBulkReject}
                                >
                                    {bulkProcessing ? "Rejecting..." : "Reject All"}
                                </Button>
                            </div>
                        </Modal>
                    </>)
            }
        </div>
    );
}
